import type { EditorView } from "prosemirror-view";

import type { EditorContext } from "../context";
import {
  insertCurrentDate,
  insertMathBlock,
  insertRule,
  insertTable,
  toggleBlockquote,
  toggleCodeBlock,
  toggleHeading,
  toggleNotice,
} from "../commands/blocks";
import { BULLET_LIST, TODO_LIST, orderedList, toggleList } from "../commands/lists";
import { wrapInToggle } from "../commands/containers";
import { pickFiles, uploadFiles } from "../plugins/upload";
import type { Command } from "prosemirror-state";

/**
 * The block menu, in the order and with the labels and keywords of
 * `editor.md` §2. `group` numbers reproduce the separators in that table:
 * a separator is drawn wherever the group changes, and never first, last, or
 * twice in a row after filtering.
 */
export interface SlashItem {
  id: string;
  title: string;
  keywords: readonly string[];
  shortcut?: string;
  group: number;
  run: (view: EditorView, context: EditorContext) => void;
}

function command(run: Command): SlashItem["run"] {
  return (view) => {
    run(view.state, view.dispatch, view);
    view.focus();
  };
}

function uploader(accept: string, options: { preview?: boolean } = {}): SlashItem["run"] {
  return (view, context) => {
    void pickFiles(accept, true).then(async (files) => {
      if (files.length === 0) return;
      await uploadFiles(view, files, view.state.selection.from, context);
      if (options.preview === true) return;
    });
  };
}

export const SLASH_ITEMS: readonly SlashItem[] = [
  {
    id: "heading-1",
    title: "Big heading",
    keywords: ["h1", "heading1", "title", "header"],
    shortcut: "^ ⇧ 1",
    group: 1,
    run: command(toggleHeading(1)),
  },
  {
    id: "heading-2",
    title: "Medium heading",
    keywords: ["h2", "heading2", "header", "subtitle"],
    shortcut: "^ ⇧ 2",
    group: 1,
    run: command(toggleHeading(2)),
  },
  {
    id: "heading-3",
    title: "Small heading",
    keywords: ["h3", "heading3", "header"],
    shortcut: "^ ⇧ 3",
    group: 1,
    run: command(toggleHeading(3)),
  },
  {
    id: "heading-4",
    title: "Extra small heading",
    keywords: ["h4", "heading4", "header"],
    shortcut: "^ ⇧ 4",
    group: 1,
    run: command(toggleHeading(4)),
  },
  {
    id: "todo-list",
    title: "Todo list",
    keywords: ["checklist", "checkbox", "task"],
    shortcut: "^ ⇧ 7",
    group: 2,
    run: command(toggleList(TODO_LIST)),
  },
  {
    id: "bulleted-list",
    title: "Bulleted list",
    keywords: ["unordered", "dash", "point"],
    shortcut: "^ ⇧ 8",
    group: 2,
    run: command(toggleList(BULLET_LIST)),
  },
  {
    id: "ordered-list",
    title: "Ordered list",
    keywords: ["ol", "numbered"],
    shortcut: "^ ⇧ 9",
    group: 2,
    run: command(toggleList(orderedList("number"))),
  },
  {
    id: "image",
    title: "Image",
    keywords: ["picture", "photo", "img", "upload", "png", "jpg", "screenshot"],
    group: 3,
    run: uploader("image/*"),
  },
  {
    id: "video",
    title: "Video",
    keywords: ["movie", "avi", "mp4", "film", "upload", "player"],
    group: 3,
    run: uploader("video/*"),
  },
  {
    id: "embed-pdf",
    title: "Embed PDF",
    keywords: ["document", "upload", "attach"],
    group: 3,
    run: uploader("application/pdf", { preview: true }),
  },
  {
    id: "file-attachment",
    title: "File attachment",
    keywords: ["upload", "attach"],
    group: 3,
    run: uploader("*/*"),
  },
  {
    id: "table",
    title: "Table",
    keywords: ["grid", "spreadsheet", "rows", "columns", "cells"],
    group: 3,
    run: (view) => {
      insertTable(3, 3, view)(view.state, view.dispatch, view);
      view.focus();
    },
  },
  {
    id: "quote",
    title: "Quote",
    keywords: ["blockquote", "pullquote", "citation"],
    shortcut: "Mod ]",
    group: 3,
    run: command(toggleBlockquote),
  },
  {
    id: "code-block",
    title: "Code block",
    keywords: ["script", "snippet", "syntax", "pre"],
    shortcut: "^ ⇧ c",
    group: 3,
    run: command(toggleCodeBlock()),
  },
  {
    id: "math-block",
    title: "Math block (LaTeX)",
    keywords: ["katex", "formula", "equation"],
    group: 3,
    run: command(insertMathBlock),
  },
  {
    id: "divider",
    title: "Divider",
    keywords: ["horizontal rule", "break", "line", "separator"],
    shortcut: "Mod _",
    group: 3,
    run: command(insertRule("---")),
  },
  {
    id: "page-break",
    title: "Page break",
    keywords: ["pagebreak", "print", "line"],
    group: 3,
    run: command(insertRule("***")),
  },
  {
    id: "current-date",
    title: "Current date",
    keywords: ["clock", "today", "time", "now"],
    group: 3,
    run: (view) => {
      insertCurrentDate()(view.state, view.dispatch, view);
      view.focus();
    },
  },
  {
    id: "toggle-block",
    title: "Toggle block",
    keywords: ["collapsible", "collapse", "fold", "accordion", "details", "expand"],
    group: 4,
    run: command(wrapInToggle(null)),
  },
  {
    id: "toggle-heading-1",
    title: "Big toggle heading",
    keywords: ["collapsible", "collapse", "fold", "accordion", "details", "expand", "h1"],
    group: 4,
    run: command(wrapInToggle(1)),
  },
  {
    id: "toggle-heading-2",
    title: "Medium toggle heading",
    keywords: ["collapsible", "collapse", "fold", "accordion", "details", "expand", "h2"],
    group: 4,
    run: command(wrapInToggle(2)),
  },
  {
    id: "toggle-heading-3",
    title: "Small toggle heading",
    keywords: ["collapsible", "collapse", "fold", "accordion", "details", "expand", "h3"],
    group: 4,
    run: command(wrapInToggle(3)),
  },
  {
    id: "toggle-heading-4",
    title: "Extra small toggle heading",
    keywords: ["collapsible", "collapse", "fold", "accordion", "details", "expand", "h4"],
    group: 4,
    run: command(wrapInToggle(4)),
  },
  {
    id: "notice-info",
    title: "Info notice",
    keywords: ["card", "callout", "hint", "information", "note"],
    group: 5,
    run: command(toggleNotice("info")),
  },
  {
    id: "notice-success",
    title: "Success notice",
    keywords: ["card", "callout", "hint"],
    group: 5,
    run: command(toggleNotice("success")),
  },
  {
    id: "notice-warning",
    title: "Warning notice",
    keywords: ["card", "callout", "hint", "error", "caution", "danger", "alert"],
    group: 5,
    run: command(toggleNotice("warning")),
  },
  {
    id: "notice-tip",
    title: "Tip notice",
    keywords: ["card", "callout", "hint", "suggestion"],
    group: 5,
    run: command(toggleNotice("tip")),
  },
  {
    id: "mermaid-diagram",
    title: "Mermaid Diagram",
    keywords: ["flowchart", "graph", "sequence", "gantt"],
    group: 6,
    run: command(toggleCodeBlock("mermaid")),
  },
];

/**
 * §2 and its open question 1: a plain case-insensitive substring test over the
 * title and the keyword list, order preserved, until the oracle shows
 * otherwise.
 */
export function filterSlashItems(
  query: string,
  items: readonly SlashItem[] = SLASH_ITEMS,
): SlashItem[] {
  const needle = query.trim().toLowerCase();
  if (needle === "") return [...items];
  return items.filter((item) =>
    `${item.title} ${item.keywords.join(" ")}`.toLowerCase().includes(needle),
  );
}

/**
 * The reduced editor (the comment composer, §1) offers only what its schema
 * can hold: no tables, notices, fences, toggles, math or attachments.
 */
export const REDUCED_SLASH_IDS: readonly string[] = [
  "heading-1",
  "heading-2",
  "heading-3",
  "heading-4",
  "todo-list",
  "bulleted-list",
  "ordered-list",
  "image",
  "quote",
  "divider",
  "current-date",
];
